import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import Button from '../ui/Button';

const Barra = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  background: ${({ theme }) => theme.colors.bg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Contenido = styled.div`
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  padding: 0.75rem ${({ theme }) => theme.space.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.md};
`;

const Logo = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.45rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.sageDeep};
  letter-spacing: 0.04em;

  span {
    color: ${({ theme }) => theme.colors.sage};
  }
`;

const Enlaces = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: fixed;
    inset: 0;
    top: 61px;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    background: ${({ theme }) => theme.colors.bg};
    padding: ${({ theme }) => theme.space.md};
    transform: translateX(${({ $abierto }) => ($abierto ? '0' : '100%')});
    transition: transform 250ms ease;
    overflow-y: auto;
  }
`;

const Enlace = styled(NavLink)`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 0.4rem 0;
  border-bottom: 2px solid transparent;
  transition: color 200ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }

  &.active {
    color: ${({ theme }) => theme.colors.sageDeep};
    border-bottom-color: ${({ theme }) => theme.colors.sage};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0.9rem 0.5rem;
    font-size: 1.05rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};

    &.active {
      border-bottom-color: ${({ theme }) => theme.colors.border};
    }
  }
`;

const Acciones = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    align-items: stretch;
    padding-top: ${({ theme }) => theme.space.md};
  }
`;

const BotonMenu = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: ${({ theme }) => theme.colors.sageDeep};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`;

const rutas = [
  { a: '/', texto: 'Inicio', exacta: true },
  { a: '/nosotros', texto: 'Nosotros' },
  { a: '/clases', texto: 'Clases' },
  { a: '/agenda', texto: 'Agenda' },
  { a: '/instructores', texto: 'Instructores' },
  { a: '/paquetes', texto: 'Paquetes' },
  { a: '/contacto', texto: 'Contacto' },
];

function IconoMenu({ abierto }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      {abierto ? (
        <>
          <line x1="5" y1="5" x2="19" y2="19" />
          <line x1="19" y1="5" x2="5" y2="19" />
        </>
      ) : (
        <>
          <line x1="4" y1="7" x2="20" y2="7" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="17" x2="20" y2="17" />
        </>
      )}
    </svg>
  );
}

function Navbar() {
  const [abierto, setAbierto] = useState(false);
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const cerrar = () => setAbierto(false);

  const salir = () => {
    logout();
    cerrar();
    navigate('/');
  };

  return (
    <Barra>
      <Contenido>
        <Logo to="/" onClick={cerrar}>
          numa<span>.</span>
        </Logo>
        <BotonMenu
          onClick={() => setAbierto((v) => !v)}
          aria-label={abierto ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={abierto}
        >
          <IconoMenu abierto={abierto} />
        </BotonMenu>
        <Enlaces $abierto={abierto}>
          {rutas.map(({ a, texto, exacta }) => (
            <Enlace key={a} to={a} end={exacta} onClick={cerrar}>
              {texto}
            </Enlace>
          ))}
          <Acciones>
            {usuario ? (
              <>
                <Button as={Link} to="/mi-cuenta/reservas" $variante="secundario" $chico onClick={cerrar}>
                  Mi cuenta
                </Button>
                <Button $variante="fantasma" $chico onClick={salir}>
                  Salir
                </Button>
              </>
            ) : (
              <>
                <Button as={Link} to="/login" $variante="fantasma" $chico onClick={cerrar}>
                  Iniciar sesión
                </Button>
                <Button as={Link} to="/agenda" $chico onClick={cerrar}>
                  Reserva tu clase
                </Button>
              </>
            )}
          </Acciones>
        </Enlaces>
      </Contenido>
    </Barra>
  );
}

export default Navbar;
